/**
 * Bucket B opt 3 — investment requests (the DEMAND board). Investors browse what
 * developers/investors are seeking (land, buildings to develop). Reuses the
 * existing Investors-Hub apply form at the bottom (the "express interest" path).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import { GooeyLoader } from '../../components/ui/gooey-loader';
import ApplyForm from './ApplyForm';
import { fetchInvestmentRequests, type InvestmentRequestItem } from '../../services/investorsService';
import { formatAmount } from '../../utils/numberFormat';

const PAGE_SIZE = 12;

const CITIES = [
  { value: '', en: 'All cities', ar: 'كل المدن' },
  { value: 'Riyadh', en: 'Riyadh', ar: 'الرياض' },
  { value: 'Jeddah', en: 'Jeddah', ar: 'جدة' },
  { value: 'Makkah', en: 'Makkah', ar: 'مكة المكرمة' },
  { value: 'Madinah', en: 'Madinah', ar: 'المدينة المنورة' },
  { value: 'Dammam', en: 'Dammam', ar: 'الدمام' },
];

const humanize = (s: string) => s.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

export default function InvestmentRequestsPage() {
  const { i18n } = useTranslation('common');
  const isRTL = i18n.dir() === 'rtl';

  const [city, setCity] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<InvestmentRequestItem[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => { setPage(1); }, [city]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    fetchInvestmentRequests({ city: city || undefined, page, pageSize: PAGE_SIZE })
      .then((res) => { if (cancelled) return; setItems(res.data); setTotalPages(res.meta.totalPages || 1); })
      .catch(() => { if (cancelled) return; setItems([]); setTotalPages(1); setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [city, page]);

  const goToPage = (target: number) => {
    const next = Math.min(Math.max(1, target), totalPages);
    if (next !== page) { setPage(next); window.scrollTo({ top: 0, behavior: 'smooth' }); }
  };

  const priceRange = (min: number | null, max: number | null) => {
    if (min == null && max == null) return null;
    const fmt = (n: number) => formatAmount(n);
    const sar = isRTL ? 'ريال' : 'SAR';
    if (min != null && max != null) return `${fmt(min)} - ${fmt(max)} ${sar}`;
    return `${fmt((min ?? max)!)} ${sar}`;
  };

  return (
    <div className="min-h-screen bg-white" dir={i18n.dir()}>
      <Navigation />
      <main id="main-content" className="pb-20">
        <section className="bg-[#003930] py-12 text-white">
          <div className="mx-auto max-w-6xl px-4 text-center">
            <Link to="/investors" className="mb-3 inline-flex items-center gap-1.5 text-xs font-medium text-white/80 hover:text-white">
              <i className={isRTL ? 'ri-arrow-right-line' : 'ri-arrow-left-line'} aria-hidden="true" />{isRTL ? 'منصة الاستثمار' : 'Investment hub'}
            </Link>
            <h1 className="font-display text-3xl font-bold sm:text-4xl"><i className="ri-search-eye-line mx-2" aria-hidden="true" />{isRTL ? 'طلبات الاستثمار العقاري' : 'Real-estate investment requests'}</h1>
            <p className="mt-3 text-sm text-white/85 sm:text-base">
              {isRTL ? 'مطوّرون ومستثمرون يبحثون عن أراضٍ ومبانٍ للتطوير - اطّلع على الطلبات وعبّر عن اهتمامك.' : 'Developers & investors seeking land & buildings to develop - browse the requests and express your interest.'}
            </p>
          </div>
        </section>

        <div className="mx-auto max-w-6xl px-4 py-6">
          {/* City filter */}
          <div className="mb-6 flex flex-wrap gap-1.5">
            {CITIES.map((c) => (
              <button key={c.value || 'all'} type="button" onClick={() => setCity(c.value)}
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${city === c.value ? 'bg-[#0D201C] text-white' : 'border border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}>
                {isRTL ? c.ar : c.en}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="flex justify-center py-20"><GooeyLoader primaryColor="#004D40" secondaryColor="#3C9F8F" borderColor="#E0E9E7" /></div>
          ) : items.length === 0 ? (
            <div className="rounded-2xl border border-slate-200 bg-white py-16 text-center">
              <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-[var(--pv-color-surface-sunken)]"><i className="ri-search-eye-line text-2xl text-[#004D40]" aria-hidden="true" /></div>
              <p className="font-display text-lg font-semibold text-slate-800">
                {error ? (isRTL ? 'تعذّر تحميل الطلبات' : 'Could not load requests') : (isRTL ? 'لا توجد طلبات استثمار بعد' : 'No investment requests yet')}
              </p>
              <p className="mx-auto mt-1 max-w-md text-sm text-slate-500">
                {isRTL ? 'عبّر عن اهتمامك بالأسفل ليصلك ما يناسبك.' : 'Register your interest below and we’ll match you.'}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {items.map((r) => (
                <div key={r.id} className={`rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm ${isRTL ? 'text-right' : ''}`}>
                  <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-[var(--pv-color-surface-sunken)] text-[#003930]"><i className="ri-funds-line" aria-hidden="true" /></span>
                    <div className="min-w-0">
                      <h3 className="truncate text-sm font-semibold text-slate-900">{humanize(r.propertyType)}</h3>
                      <p className="text-xs text-slate-400">{isRTL ? 'طلب استثمار' : 'Investment request'}</p>
                    </div>
                  </div>
                  <div className={`mt-3 flex flex-wrap gap-2 text-xs ${isRTL ? 'flex-row-reverse' : ''}`}>
                    {(r.cityAr || r.city) && <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-slate-600"><i className="ri-map-pin-2-line" aria-hidden="true" />{isRTL ? (r.cityAr || r.city) : r.city}</span>}
                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2.5 py-1 text-slate-600">{humanize(r.listingType)}</span>
                  </div>
                  {priceRange(r.minPrice, r.maxPrice) && (
                    <div className="mt-3 text-sm font-semibold text-[#003930]">{priceRange(r.minPrice, r.maxPrice)}</div>
                  )}
                  {r.notes && <p className="mt-2 line-clamp-3 text-xs leading-relaxed text-slate-500">{r.notes}</p>}
                </div>
              ))}
            </div>
          )}

          {!loading && totalPages > 1 && (
            <div className={`mt-8 flex flex-wrap items-center justify-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
              <button onClick={() => goToPage(page - 1)} disabled={page <= 1} className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-40">{isRTL ? 'السابق' : 'Prev'}</button>
              <span className="px-2 text-sm text-slate-500">{isRTL ? `صفحة ${page} من ${totalPages}` : `Page ${page} of ${totalPages}`}</span>
              <button onClick={() => goToPage(page + 1)} disabled={page >= totalPages} className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 disabled:opacity-40">{isRTL ? 'التالي' : 'Next'}</button>
            </div>
          )}

          {/* Express interest — the existing apply form folds in here */}
          <section className="mt-12 rounded-3xl border border-slate-200/80 bg-[var(--pv-color-surface-strong)] p-6 sm:p-8">
            <div className="mb-6 text-center">
              <h2 className="font-display text-2xl font-bold text-[var(--pv-color-primary)]">{isRTL ? 'سجّل اهتمامك' : 'Register your interest'}</h2>
              <p className="mt-1 text-sm text-slate-500">{isRTL ? 'أخبرنا بما تبحث عنه وسيتواصل معك فريقنا.' : 'Tell us what you’re looking for and our team will be in touch.'}</p>
            </div>
            <div className="mx-auto max-w-3xl"><ApplyForm isRTL={isRTL} /></div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}
